import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Download, Loader2, ImageOff, Search, X, Package, Check,
} from 'lucide-react';

/**
 * PastPerfectStudio — Past Perfect (pastperfectstore.co) Çerçeve Stüdyosu
 *
 * Katalogdan ürün seç → story (1080x1920) + post (1080x1080) fiyatlı çerçeveli
 * görsel üret → PNG indir. Tüm görsel işleme client-side canvas'ta yapılır;
 * backend sadece veri (/api/pastperfect/catalog) + görsel proxy sağlar.
 * (Kalıp: YatasFrameStudio.js)
 */

const API = 'https://api.glowytics.com';
const proxied = (url) => `${API}/api/pastperfect/image-proxy?url=${encodeURIComponent(url)}`;

// Çıktı boyutları
const SIZES = [
  { id: 'post', label: 'Post', sub: '1:1 · 1080×1080', w: 1080, h: 1080 },
  { id: 'story', label: 'Story', sub: '9:16 · 1080×1920', w: 1080, h: 1920 },
];

// Başlık serifi. Referans post'taki tipografi sitenin Newsreader'ından daha ince
// ve "moda editoryali" karakterinde → EB Garamond varsayılan, Newsreader (site
// fontu) alternatif.
//
// Cormorant/Playfair daha da yakın duruyordu AMA varsayılan rakamları eski-stil
// ("KELLY 20" → "KELLY 2o"). Katalogda model numarası bol (Veto 100, 24/24 29) ve
// canvas font-feature-settings desteklemediği için `lnum` ile düzeltilemiyor →
// fontun varsayılanı lining olmak zorunda. EB Garamond ve Newsreader lining.
const TITLE_FONTS = [
  { id: 'garamond', label: 'EB Garamond', stack: '"EB Garamond", Georgia, serif', weight: 400 },
  { id: 'newsreader', label: 'Newsreader', stack: 'Newsreader, Georgia, serif', weight: 400 },
];

const BODY_FONT = '"Plus Jakarta Sans", Arial, sans-serif';

// Varsayılan alt metin — referans post'un kendi kopyası. Ürüne göre değiştirilebilir
// (fiyat/durum veriden gelir ama slogan pazarlama kopyası, uydurmuyoruz).
const DEFAULT_TAGLINE = 'PERFECTION DOESN’T WAIT.';

const trMoney = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
const fmtPrice = (v, cur) => {
  if (v == null || isNaN(v)) return '';
  const sym = cur === 'TRY' || cur === 'TL' ? '₺' : cur ? cur + ' ' : '';
  return sym + trMoney.format(Math.round(v));
};

// ---------------------------------------------------------------------------
// Canvas yardımcıları
// ---------------------------------------------------------------------------

// Görselin kaynak dikdörtgenini "contain" ile kutuya oturt (oranı bozmadan).
// Çizilen hedef alanı döner.
function drawContain(ctx, img, src, x, y, w, h) {
  const ir = src.w / src.h;
  let dw = w;
  let dh = h;
  if (ir > w / h) dh = w / ir;
  else dw = h * ir;
  const dx = x + (w - dw) / 2;
  const dy = y + (h - dh) / 2;
  ctx.drawImage(img, src.x, src.y, src.w, src.h, dx, dy, dw, dh);
  return { x: dx, y: dy, w: dw, h: dh };
}

// --- Ürün fotoğrafı analizi -------------------------------------------------
// Katalog fotoğraflarının zemini düz (≈#F5F5F5) ve kenar boşlukları çok geniş:
// ham hâlde ürün karenin ortasında minicik kalıyor, ayrıca sabit beyaz bir kart
// çizince fotoğrafın kendi zemini kartla aynı olmadığı için içeride hayalet bir
// dikdörtgen beliriyor. Bu yüzden zemin rengini fotoğraftan ölçüp (kart onunla
// kusursuz kaynaşsın) ürünün sınırlarını bulup boş kenarları kırpıyoruz.
// Analiz küçültülmüş kopyada yapılır (1500² pikseli taramamak için), sonuç tam
// çözünürlüğe geri ölçeklenir. Sonuç img üzerinde cache'lenir (post+story aynı).
const analysisCache = new WeakMap();

function analyzeProductImage(img) {
  const hit = analysisCache.get(img);
  if (hit) return hit;

  const full = { x: 0, y: 0, w: img.width, h: img.height };
  let result = { bg: null, crop: full };

  try {
    const S = 200;
    const ir = img.width / img.height;
    const w = Math.max(1, ir > 1 ? S : Math.round(S * ir));
    const h = Math.max(1, ir > 1 ? Math.round(S / ir) : S);
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.drawImage(img, 0, 0, w, h);
    const d = cx.getImageData(0, 0, w, h).data;
    const at = (x, y) => {
      const i = (y * w + x) * 4;
      return [d[i], d[i + 1], d[i + 2]];
    };

    // zemin = dört köşenin ortancası (kanal bazında)
    const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
    const bg = [0, 1, 2].map((k) => {
      const vals = corners.map((p) => p[k]).sort((a, b) => a - b);
      return Math.round((vals[1] + vals[2]) / 2);
    });

    // Köşeler birbirinden farklıysa düz zemin yok (lifestyle çekim) → kırpma yok
    const spread = Math.max(
      ...[0, 1, 2].map((k) => Math.max(...corners.map((p) => p[k])) - Math.min(...corners.map((p) => p[k])))
    );
    if (spread <= 24) {
      const TOL = 26; // JPEG gürültüsü + yumuşak gölge payı
      let x0 = w, y0 = h, x1 = -1, y1 = -1;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const p = at(x, y);
          const dist = Math.abs(p[0] - bg[0]) + Math.abs(p[1] - bg[1]) + Math.abs(p[2] - bg[2]);
          if (dist > TOL) {
            if (x < x0) x0 = x;
            if (y < y0) y0 = y;
            if (x > x1) x1 = x;
            if (y > y1) y1 = y;
          }
        }
      }
      const found = x1 >= x0 && y1 >= y0;
      // Ürün karenin ≥%92'sini kaplıyorsa kırpacak boşluk yok demektir
      const worth = found && ((x1 - x0 + 1) / w < 0.92 || (y1 - y0 + 1) / h < 0.92);
      if (worth) {
        const sx = img.width / w;
        const sy = img.height / h;
        const padX = (x1 - x0 + 1) * 0.04;
        const padY = (y1 - y0 + 1) * 0.04;
        const cx0 = Math.max(0, (x0 - padX) * sx);
        const cy0 = Math.max(0, (y0 - padY) * sy);
        const cx1 = Math.min(img.width, (x1 + 1 + padX) * sx);
        const cy1 = Math.min(img.height, (y1 + 1 + padY) * sy);
        if (cx1 - cx0 > 8 && cy1 - cy0 > 8) {
          result = { bg: `rgb(${bg[0]},${bg[1]},${bg[2]})`, crop: { x: cx0, y: cy0, w: cx1 - cx0, h: cy1 - cy0 } };
        }
      }
      if (!result.bg) result = { bg: `rgb(${bg[0]},${bg[1]},${bg[2]})`, crop: full };
    }
  } catch {
    // getImageData taint'te patlar → analizsiz devam (ham görsel yine çizilir)
  }

  analysisCache.set(img, result);
  return result;
}

// CORS-safe yükle (canvas taint olmasın → toBlob çalışsın)
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('img yüklenemedi: ' + src));
    im.src = src;
  });
}

// Metni kelime kelime sar → satır dizisi (maxLines aşılırsa son satır …)
function wrapLines(ctx, text, maxW, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? line + ' ' + word : word;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  if (maxLines && lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    let last = kept[maxLines - 1];
    while (last && ctx.measureText(last + '…').width > maxW && last.includes(' ')) {
      last = last.slice(0, last.lastIndexOf(' '));
    }
    kept[maxLines - 1] = last + '…';
    return kept;
  }
  return lines;
}

// --- Harf aralıklı (tracked) metin --------------------------------------------
// Tipografinin tamamı versal + geniş aralıklı olduğu için tracking her yerde şart.
// ctx.letterSpacing Chrome 99+; yoksa harf harf çizilir.
const hasLS = (ctx) => 'letterSpacing' in ctx;

// Tracked metnin genişliği. Chrome letterSpacing'i SON harften sonra da eklediği
// için ölçümden bir tracking düşülür, yoksa ortalama sağa kayar.
function measureTracked(ctx, s, tracking) {
  if (!s) return 0;
  if (hasLS(ctx)) {
    const prev = ctx.letterSpacing;
    ctx.letterSpacing = `${tracking}px`;
    const w = ctx.measureText(s).width - tracking;
    ctx.letterSpacing = prev;
    return w;
  }
  return [...s].reduce((a, c) => a + ctx.measureText(c).width + tracking, 0) - tracking;
}

// Tracked metni x'ten SOLA yaslı çiz, çizilen genişliği döner.
function fillTrackedAt(ctx, s, x, y, tracking) {
  if (!s) return 0;
  const w = measureTracked(ctx, s, tracking);
  ctx.textAlign = 'left';
  if (hasLS(ctx)) {
    const prev = ctx.letterSpacing;
    ctx.letterSpacing = `${tracking}px`;
    ctx.fillText(s, x, y);
    ctx.letterSpacing = prev;
  } else {
    let cx = x;
    for (const c of [...s]) {
      ctx.fillText(c, cx, y);
      cx += ctx.measureText(c).width + tracking;
    }
  }
  return w;
}

// Tracked metni ortalayarak çiz
function fillTracked(ctx, s, cx, y, tracking) {
  fillTrackedAt(ctx, s, cx - measureTracked(ctx, s, tracking) / 2, y, tracking);
}

// Farklı font/ağırlıktaki parçaları TEK satır olarak ortalayıp çiz.
// (referanstaki "ENTRUPY VERIFIED. ONE OF ONE" — ilk parça kalın, ikincisi normal)
function fillSegments(ctx, segs, cx, y, tracking) {
  const parts = segs.filter((s) => s.text);
  if (!parts.length) return;
  let total = 0;
  for (const p of parts) {
    ctx.font = p.font;
    total += measureTracked(ctx, p.text, tracking);
  }
  let x = cx - total / 2;
  for (const p of parts) {
    ctx.font = p.font;
    ctx.fillStyle = p.color;
    x += fillTrackedAt(ctx, p.text, x, y, tracking);
  }
}

// Siyah fiyat kutusu (referanstaki chip): metin + cömert yatay pay
function drawPriceChip(ctx, text, cx, cy, { fontSize, tracking, padX, padY, bg, fg }) {
  ctx.font = `600 ${fontSize}px ${BODY_FONT}`;
  const tw = measureTracked(ctx, text, tracking);
  const w = tw + padX * 2;
  const h = fontSize + padY * 2;
  ctx.fillStyle = bg;
  ctx.fillRect(cx - w / 2, cy - h / 2, w, h);
  ctx.fillStyle = fg;
  ctx.textBaseline = 'middle';
  fillTracked(ctx, text, cx, cy + 1, tracking);
  ctx.textBaseline = 'alphabetic';
}

// Katalog başlıkları çoğu üründe markayla başlıyor ("Jimmy Choo 'Veto 100' …").
// Marka zaten üst satırda yazıldığı için tekrar etmesin.
function stripBrandPrefix(title, brand) {
  const t = (title || '').trim();
  const b = (brand || '').trim();
  if (!t || !b) return t;
  if (t.toLowerCase().startsWith(b.toLowerCase())) {
    const rest = t.slice(b.length).replace(/^[\s\-–—:,·]+/, '');
    if (rest) return rest;
  }
  return t;
}

// Rengin açık mı koyu mu olduğu → üstüne hangi mürekkep gelecek
const isLight = (rgb) => {
  const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgb || '');
  if (!m) return true;
  const [r, g, b] = [1, 2, 3].map((i) => +m[i]);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 145;
};

// ---------------------------------------------------------------------------
// Çerçeve render — referans post düzeni (bkz. Past Perfect'in kendi gönderileri):
//   ÜSTTE marka + ürün adı (versal, geniş aralıklı serif) · ORTADA kocaman ürün ·
//   ALTTA slogan satırları · en altta siyah kutuda fiyat. Logo YOK — tipografi
//   markanın kendisi (IG'de hesap adı zaten görünüyor).
//
// Zemin, ürün fotoğrafının KENDİ zemin renginden gelir (bkz. analyzeProductImage):
// kare baştan sona o renkle dolar, kırpılmış ürün üstüne oturur → kart/çerçeve
// olmadan tam kanayan, dikişsiz bir yüzey. Sabit bir tema rengi seçilseydi
// fotoğrafın kendi zemini kutu gibi görünürdü.
// ---------------------------------------------------------------------------
function renderFrame(ctx, { size, titleFont, img, product, tagline1, tagline2 }) {
  const { w: W, h: H } = size;
  const story = size.id === 'story';
  const cx = W / 2;

  // Alt blok (slogan + fiyat kutusu) AŞAĞIDAN YUKARI kurulur: önce kutu tabana
  // sabitlenir, slogan satırları onun üstüne yığılır. Tepeden kurulursa kutu
  // sloganın üstüne biner.
  const L = story
    ? { pad: 96, titleTop: 210, title: 86, titleTrack: 15, titleGap: 1.30,
        tagSize: 30, tagTrack: 4.5, tagGap: 46, gapChip: 62,
        chipFont: 34, chipTrack: 3, chipPadX: 52, chipPadY: 26, chipBottom: 168 }
    : { pad: 76, titleTop: 118, title: 58, titleTrack: 10, titleGap: 1.28,
        tagSize: 21, tagTrack: 3, tagGap: 32, gapChip: 40,
        chipFont: 25, chipTrack: 2.2, chipPadX: 38, chipPadY: 18, chipBottom: 92 };

  const meta = analyzeProductImage(img);
  const bg = meta.bg || '#F2F1EE';
  const ink = isLight(bg) ? '#171614' : '#F7F5F2';
  const chipBg = isLight(bg) ? '#111110' : '#F7F5F2';
  const chipFg = isLight(bg) ? '#FFFFFF' : '#111110';

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = ink;

  // --- ÜST: marka + ürün adı, versal, geniş aralıklı
  const maxTitleW = W - L.pad * 2;
  const titleFontStr = (px) => `${titleFont.weight} ${px}px ${titleFont.stack}`;

  // Tracked metin ölçüsü normal measureText'ten geniş → sığana kadar küçült
  const fitTracked = (text, startPx, minPx) => {
    let px = startPx;
    for (; px > minPx; px -= 2) {
      ctx.font = titleFontStr(px);
      if (measureTracked(ctx, text, L.titleTrack) <= maxTitleW) break;
    }
    return px;
  };

  const brandLine = (product.brand || '').toUpperCase();
  const nameSource = product.displayTitle != null
    ? product.displayTitle
    : stripBrandPrefix(product.title, product.brand);
  const nameLines = (() => {
    ctx.font = titleFontStr(L.title);
    // sarma ölçüsünü tracking payıyla daralt (wrapLines tracking bilmez)
    const budget = maxTitleW * (1 - L.titleTrack / (L.title * 0.72));
    return wrapLines(ctx, nameSource.toUpperCase(), budget, story ? 3 : 2);
  })();

  const allLines = [brandLine, ...nameLines].filter(Boolean);
  const titlePx = allLines.reduce((px, t) => Math.min(px, fitTracked(t, L.title, 26)), L.title);
  const lineH = titlePx * L.titleGap;

  let y = L.titleTop + titlePx;
  ctx.fillStyle = ink;
  for (const line of allLines) {
    ctx.font = titleFontStr(titlePx);
    fillTracked(ctx, line, cx, y, L.titleTrack);
    y += lineH;
  }
  const titleBottom = y - lineH + titlePx * 0.3;

  // --- FİYAT: siyah kutu, tabana sabit
  const chipH = L.chipFont + L.chipPadY * 2;
  const chipTop = H - L.chipBottom - chipH;
  drawPriceChip(ctx, fmtPrice(product.price, product.currency), cx, chipTop + chipH / 2, {
    fontSize: L.chipFont, tracking: L.chipTrack, padX: L.chipPadX, padY: L.chipPadY,
    bg: chipBg, fg: chipFg,
  });

  // --- SLOGAN: kutunun üstüne yığılır (ikincisinin ilk cümlesi kalın — referanstaki gibi)
  const t1 = (tagline1 || '').trim().toUpperCase();
  const t2 = (tagline2 || '').trim().toUpperCase();
  ctx.fillStyle = ink;

  // baseline'lar aşağıdan yukarı: kutu üstü → t2 → t1
  const y2 = chipTop - L.gapChip;
  const y1 = t2 ? y2 - L.tagGap : y2;
  let tagTop = chipTop;

  if (t2) {
    // ilk cümle (ilk noktaya kadar) kalın, kalanı normal
    const m = /^([^.]*\.)\s*(.*)$/.exec(t2);
    const segs = m
      ? [{ text: m[1], font: `700 ${L.tagSize}px ${BODY_FONT}`, color: ink },
         { text: (m[2] ? '  ' + m[2] : ''), font: `500 ${L.tagSize}px ${BODY_FONT}`, color: ink }]
      : [{ text: t2, font: `500 ${L.tagSize}px ${BODY_FONT}`, color: ink }];
    fillSegments(ctx, segs, cx, y2, L.tagTrack);
    tagTop = y2 - L.tagSize;
  }
  if (t1) {
    ctx.font = `500 ${L.tagSize}px ${BODY_FONT}`;
    ctx.fillStyle = ink;
    fillTracked(ctx, t1, cx, y1, L.tagTrack);
    tagTop = y1 - L.tagSize;
  }

  // --- ORTA: ürün, başlık ile slogan arasındaki tüm alanı kaplar
  const boxTop = titleBottom + (story ? 60 : 34);
  const boxH = tagTop - boxTop;
  if (boxH > 80) {
    drawContain(ctx, img, meta.crop, L.pad, boxTop, W - L.pad * 2, boxH);
  }
}


// ---------------------------------------------------------------------------
// Tek çerçeve önizleme + indirme
// ---------------------------------------------------------------------------
function FrameCanvas({ product, size, titleFont, tagline1, tagline2, fontsReady, registerCanvas }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    if (!fontsReady) return undefined;
    setStatus('loading');

    loadImage(proxied(product.imageUrl))
      .then((img) => {
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, size.w, size.h);
        renderFrame(ctx, { size, titleFont, img, product, tagline1, tagline2 });
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('frame render error', e);
        setStatus('error');
      });

    return () => { cancelled = true; };
  }, [product, size, titleFont, tagline1, tagline2, fontsReady]);

  const filename = useCallback(
    () => `pastperfect_${size.id}_${product.productCode || product.handle}.png`,
    [size, product]
  );

  const download = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || status !== 'ready') return;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('İndirilemedi'); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename();
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [status, filename]);

  useEffect(() => {
    registerCanvas?.(size.id, { download, ready: status === 'ready' });
  }, [size.id, download, status, registerCanvas]);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-sm font-semibold text-gray-900">{size.label}</span>
          <span className="ml-2 text-xs text-gray-400">{size.sub}</span>
        </div>
        <button
          onClick={download}
          disabled={status !== 'ready'}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-medium disabled:opacity-40 hover:bg-black transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> İndir
        </button>
      </div>
      <div
        className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50 mx-auto"
        style={{ aspectRatio: `${size.w}/${size.h}`, maxHeight: 560 }}
      >
        <canvas
          ref={canvasRef}
          width={size.w}
          height={size.h}
          className="w-full h-full block"
          style={{ display: status === 'ready' ? 'block' : 'none' }}
        />
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-300">
            <Loader2 className="w-7 h-7 animate-spin" />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2 p-4 text-center">
            <ImageOff className="w-7 h-7" />
            <span className="text-xs">Görsel yüklenemedi</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sayfa
// ---------------------------------------------------------------------------
export default function PastPerfectStudio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState('');
  const [brand, setBrand] = useState('all');
  const [category, setCategory] = useState('all');
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [titleFont, setTitleFont] = useState(TITLE_FONTS[0]);
  const [tagline1, setTagline1] = useState(DEFAULT_TAGLINE);
  // null = kullanıcı dokunmadı → üründen türet. '' = bilerek sildi.
  const [tagline2, setTagline2] = useState(null);
  const [titleOverride, setTitleOverride] = useState(null);

  const [fontsReady, setFontsReady] = useState(false);
  const canvasApi = useRef({});
  const registerCanvas = useCallback((id, api) => { canvasApi.current[id] = api; }, []);

  // --- fontlar: canvas metni ölçmeden ÖNCE yüklenmeli, yoksa sessizce
  // fallback'e düşer ve ölçüm/sarma yanlış olur (satır sarma bozulur).
  useEffect(() => {
    const ID = 'pp-studio-fonts';
    if (!document.getElementById(ID)) {
      const l = document.createElement('link');
      l.id = ID;
      l.rel = 'stylesheet';
      l.href =
        'https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(l);
    }
    const want = [
      '400 86px "EB Garamond"', '400 58px "EB Garamond"',
      '400 86px Newsreader', '400 58px Newsreader',
      '500 30px "Plus Jakarta Sans"', '700 30px "Plus Jakarta Sans"',
      '600 34px "Plus Jakarta Sans"',
    ];
    Promise.all(want.map((f) => document.fonts.load(f).catch(() => null)))
      .then(() => document.fonts.ready)
      .then(() => setFontsReady(true))
      .catch(() => setFontsReady(true)); // font gelmese de Georgia/Arial ile çiz
  }, []);

  // --- katalog
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/pastperfect/catalog`, { credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setData(j);
      const first = (j.products || []).find((p) => p.imageUrl);
      if (first) setSelected(first);
    } catch (e) {
      setError(e.message);
      toast.error('Katalog yüklenemedi: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const list = data?.products || [];
    const needle = q.trim().toLowerCase();
    return list.filter((p) => {
      if (!p.imageUrl) return false;
      if (brand !== 'all' && p.brand !== brand) return false;
      if (category !== 'all' && p.category !== category) return false;
      if (!needle) return true;
      return [p.title, p.brand, p.productCode, p.category]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [data, q, brand, category]);

  // Seçili ürünün galeri görselleri arasından aktif olanı çerçeveye ver
  const product = useMemo(() => {
    if (!selected) return null;
    const imgs = selected.images?.length ? selected.images : [selected.imageUrl];
    return {
      ...selected,
      imageUrl: imgs[Math.min(imgIdx, imgs.length - 1)],
      displayTitle: titleOverride,
    };
  }, [selected, imgIdx, titleOverride]);

  // Slogan 2. satırı: kullanıcı yazana kadar ürünün durumunu takip eder
  const autoTagline2 = selected?.conditionLabel ? `${selected.conditionLabel}. ONE OF ONE` : '';
  const tag2 = tagline2 ?? autoTagline2;

  // Başlık: kullanıcı yazana kadar katalog adını (marka öneki atılmış) takip eder
  const autoTitle = selected ? stripBrandPrefix(selected.title, selected.brand) : '';
  const shownTitle = titleOverride ?? autoTitle;

  // Ürün değişince başlık ve durum satırı MUTLAKA sıfırlanır: bunlar ürüne özgü,
  // taşınırsa yeni ürüne yanlış isim/durum yazılır. Üst slogan kampanya kopyası → kalır.
  const pick = (p) => {
    setSelected(p);
    setImgIdx(0);
    setTitleOverride(null);
    setTagline2(null);
  };

  const downloadBoth = () => {
    const ready = SIZES.map((s) => canvasApi.current[s.id]).filter((a) => a?.ready);
    if (ready.length === 0) { toast.error('Görseller henüz hazır değil'); return; }
    ready.forEach((a, i) => setTimeout(() => a.download(), i * 250));
    toast.success(`${ready.length} görsel indiriliyor`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 font-medium">Katalog yüklenemedi</p>
        <p className="text-sm text-gray-500 mt-1">{error}</p>
        <button onClick={fetchData} className="mt-4 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm">
          Tekrar dene
        </button>
      </div>
    );
  }

  const s = data?.summary;
  const galleryImgs = selected?.images?.length ? selected.images : selected ? [selected.imageUrl] : [];

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* başlık */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Past Perfect · Çerçeve Stüdyosu</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ürün seç, story ve post boyutunda fiyatlı görseli indir.
          {s && (
            <span className="ml-1">
              {s.totalProducts} ürün · {s.totalBrands} marka · katalog {data.fetchDate}
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* ---------- sol: ürün seçici ---------- */}
        <div className="min-w-0">
          <div className="relative mb-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ürün, marka veya kod ara…"
              className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
            {q && (
              <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex gap-2 mb-3">
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">Tüm markalar</option>
              {(data?.brands || []).map((b) => (
                <option key={b.label} value={b.label}>{b.label} ({b.productCount})</option>
              ))}
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="all">Tüm kategoriler</option>
              {(data?.categories || []).map((c) => (
                <option key={c.label} value={c.label}>{c.label} ({c.productCount})</option>
              ))}
            </select>
          </div>

          <p className="text-xs text-gray-400 mb-2">{filtered.length} ürün</p>

          <div className="grid grid-cols-3 gap-2 max-h-[620px] overflow-y-auto pr-1">
            {filtered.map((p) => {
              const active = selected?.productId === p.productId;
              return (
                <button
                  key={p.productId}
                  onClick={() => pick(p)}
                  title={`${p.brand} · ${p.title}`}
                  className={`relative rounded-lg overflow-hidden border-2 bg-white transition-all ${
                    active ? 'border-gray-900 ring-2 ring-gray-900/10' : 'border-gray-100 hover:border-gray-300'
                  }`}
                >
                  <div className="aspect-square bg-gray-50">
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="p-1.5 text-left">
                    <p className="text-[10px] font-semibold text-gray-500 truncate">{p.brand}</p>
                    <p className="text-[10px] text-gray-900 truncate">{p.title}</p>
                    <p className="text-[10px] font-bold text-gray-900">{fmtPrice(p.price, p.currency)}</p>
                  </div>
                  {active && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-gray-900 flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" />
                    </span>
                  )}
                  {!p.inStock && (
                    <span className="absolute top-1 left-1 px-1 py-0.5 rounded bg-black/60 text-white text-[8px] font-medium">
                      Stokta yok
                    </span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="col-span-3 py-12 text-center text-gray-400">
                <Package className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Eşleşen ürün yok</p>
              </div>
            )}
          </div>
        </div>

        {/* ---------- sağ: çerçeve önizleme ---------- */}
        <div className="min-w-0">
          {!product ? (
            <div className="h-96 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-xl">
              <p className="text-sm">Soldan bir ürün seç</p>
            </div>
          ) : (
            <>
              {/* kontroller */}
              <div className="flex flex-wrap items-center gap-4 mb-3 p-3 rounded-xl border border-gray-200 bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Başlık fontu</span>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {TITLE_FONTS.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => setTitleFont(f)}
                        className={`px-3 py-1.5 text-xs transition-colors ${
                          titleFont.id === f.id ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        style={{ fontFamily: f.stack }}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {galleryImgs.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Görsel</span>
                    <div className="flex gap-1">
                      {galleryImgs.slice(0, 10).map((src, i) => (
                        <button
                          key={src}
                          onClick={() => setImgIdx(i)}
                          className={`w-8 h-8 rounded border-2 overflow-hidden bg-white ${
                            imgIdx === i ? 'border-gray-900' : 'border-gray-200'
                          }`}
                        >
                          <img src={src} alt="" className="w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={downloadBoth}
                  className="ml-auto flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black transition-colors"
                >
                  <Download className="w-3.5 h-3.5" /> İkisini de indir
                </button>
              </div>

              {/* başlık — katalog adı SEO'lu ve uzun ("Jimmy Choo 'Veto 100' Navy
                  Suede Sandals…"); referans postlarda kısa/küratörlü ("MINI KELLY") → elle kısalt */}
              <div className="flex gap-2 mb-2">
                <input
                  value={shownTitle}
                  onChange={(e) => setTitleOverride(e.target.value)}
                  placeholder="Ürün adı (başlıkta görünecek)"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                />
                {titleOverride !== null && (
                  <button
                    onClick={() => setTitleOverride(null)}
                    title="Katalogdaki adı geri getir"
                    className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 whitespace-nowrap"
                  >
                    Otomatik
                  </button>
                )}
              </div>

              {/* alt metin — pazarlama kopyası, veriden gelmiyor → elle yazılır.
                  2. satır boş bırakılırsa ürünün durumundan türetilir. */}
              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <input
                  value={tagline1}
                  onChange={(e) => setTagline1(e.target.value)}
                  placeholder="Üst slogan (ör. PERFECTION DOESN’T WAIT.)"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                />
                <input
                  value={tag2}
                  onChange={(e) => setTagline2(e.target.value)}
                  placeholder="Alt slogan (ilk cümle kalın çizilir)"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                />
                {tagline2 !== null && (
                  <button
                    onClick={() => setTagline2(null)}
                    title="Alt slogana ürünün durumunu tekrar yaz"
                    className="px-3 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 whitespace-nowrap"
                  >
                    Otomatik
                  </button>
                )}
              </div>

              {/* seçili ürün künyesi */}
              <div className="mb-4 text-sm">
                <span className="font-semibold text-gray-900">{selected.brand}</span>
                <span className="text-gray-400 mx-1.5">·</span>
                <span className="text-gray-700">{selected.title}</span>
                <span className="text-gray-400 mx-1.5">·</span>
                <span className="font-bold text-gray-900">{fmtPrice(selected.price, selected.currency)}</span>
                {selected.conditionLabel && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">
                    {selected.conditionLabel}
                  </span>
                )}
                {selected.productUrl && (
                  <a
                    href={selected.productUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 text-xs text-blue-600 hover:underline"
                  >
                    ürüne git ↗
                  </a>
                )}
              </div>

              {/* çerçeveler */}
              <div className="flex flex-col sm:flex-row gap-5 items-start">
                {SIZES.map((size) => (
                  <FrameCanvas
                    key={size.id}
                    product={product}
                    size={size}
                    titleFont={titleFont}
                    tagline1={tagline1}
                    tagline2={tag2}
                    fontsReady={fontsReady}
                    registerCanvas={registerCanvas}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
